/**
 * --------------------------------------------------------------------------
 * Bootstrap dom/data.ts
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
 * --------------------------------------------------------------------------
 */

/**
 * Constants
 */

const elementMap = new Map<Element, Map<string, unknown>>()
const comboboxMenuKeys = new Set(['bs.combobox', 'bs.menu'])

export default {
  set(element: Element, key: string, instance: unknown): void {
    if (!elementMap.has(element)) {
      elementMap.set(element, new Map())
    }

    const instanceMap = elementMap.get(element)!

    // Combobox owns a Menu on its toggle; both must remain registered for teardown.
    const isComboboxMenu = comboboxMenuKeys.has(key) &&
      [...instanceMap.keys()].every(boundKey => comboboxMenuKeys.has(boundKey))
    if (!instanceMap.has(key) && instanceMap.size !== 0 && !isComboboxMenu) {
      // eslint-disable-next-line no-console
      console.error(`Bootstrap doesn't allow more than one instance per element. Bound instance: ${[...instanceMap.keys()][0]}.`)
      return
    }

    instanceMap.set(key, instance)
  },

  get(element: Element | null, key: string): any {
    if (element && elementMap.has(element)) {
      return elementMap.get(element)!.get(key) || null
    }

    return null
  },

  getAny(element: Element | null): any {
    if (element && elementMap.has(element)) {
      return elementMap.get(element)!.values().next().value || null
    }

    return null
  },

  remove(element: Element, key: string): void {
    if (!elementMap.has(element)) {
      return
    }

    const instanceMap = elementMap.get(element)!

    instanceMap.delete(key)

    // free up element references if there are no instances left for an element
    if (instanceMap.size === 0) {
      elementMap.delete(element)
    }
  }
}
